import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Users, 
  Mail, 
  AlertTriangle, 
  CheckCircle, 
  User,
  Calendar,
  ArrowRight
} from 'lucide-react';
import { FamilyDuplicate, ParentDuplicate } from '@/services/familyValidationService';

interface FamilyDuplicateResolutionProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  familyName: string;
  duplicates: FamilyDuplicate[];
  suggestions?: string[];
  onUseExisting: (familyId: string) => void;
  onCreateWithNewName: (newName: string) => void;
  onContinueAnyway: () => void;
}

export const FamilyDuplicateResolutionDialog: React.FC<FamilyDuplicateResolutionProps> = ({
  open,
  onOpenChange,
  familyName,
  duplicates,
  suggestions,
  onUseExisting,
  onCreateWithNewName,
  onContinueAnyway,
}) => {
  const [newFamilyName, setNewFamilyName] = React.useState(`${familyName} 2`);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Family Name Already Exists
          </DialogTitle>
          <DialogDescription>
            We found existing families with similar names. What would you like to do?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Existing Families */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Existing Families</h4>
            <div className="space-y-2">
              {duplicates.map((family) => (
                <Card key={family.id} className="border-l-4 border-l-amber-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{family.name}</p>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Created {new Date(family.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => onUseExisting(family.id)}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        Use This Family
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Suggestions */}
          {suggestions && suggestions.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Similar Families</h4>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((suggestion, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="cursor-pointer hover:bg-gray-200"
                    onClick={() => onUseExisting(suggestion)}
                  >
                    {suggestion}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Resolution Options */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Resolution Options</h4>
            
            {/* Create with new name */}
            <div className="flex items-center gap-3">
              <Input
                value={newFamilyName}
                onChange={(e) => setNewFamilyName(e.target.value)}
                placeholder="Enter new family name"
                className="flex-1"
              />
              <Button
                onClick={() => onCreateWithNewName(newFamilyName)}
                disabled={!newFamilyName.trim() || newFamilyName === familyName}
              >
                Create New
              </Button>
            </div>

            {/* Continue anyway option */}
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <span className="text-sm text-red-700">
                  Continue with "{familyName}" (creates duplicate)
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onContinueAnyway}
                className="text-red-600 border-red-200 hover:bg-red-100"
              >
                Continue Anyway
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface ParentDuplicateResolutionProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentEmail: string;
  duplicates: ParentDuplicate[];
  onAddToExistingFamily: (familyId: string) => void;
  onUseDifferentEmail: (newEmail: string) => void;
  onContinueAnyway: () => void;
}

export const ParentDuplicateResolutionDialog: React.FC<ParentDuplicateResolutionProps> = ({
  open,
  onOpenChange,
  parentEmail,
  duplicates,
  onAddToExistingFamily,
  onUseDifferentEmail,
  onContinueAnyway,
}) => {
  const [newEmail, setNewEmail] = React.useState('');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Parent Email Already Exists
          </DialogTitle>
          <DialogDescription>
            This email address is already associated with existing parents. What would you like to do?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Existing Parents */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Existing Parents</h4>
            <div className="space-y-2">
              {duplicates.map((parent) => (
                <Card key={parent.id} className="border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">
                          {parent.first_name} {parent.last_name}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {parent.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {parent.family_name}
                          </span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => onAddToExistingFamily(parent.family_id)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <ArrowRight className="h-4 w-4 mr-1" />
                        Add to Family
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Resolution Options */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Resolution Options</h4>
            
            {/* Use different email */}
            <div className="flex items-center gap-3">
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter different email address"
                className="flex-1"
              />
              <Button
                onClick={() => onUseDifferentEmail(newEmail)}
                disabled={!newEmail.trim() || newEmail === parentEmail}
              >
                Use This Email
              </Button>
            </div>

            {/* Continue anyway option */}
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <span className="text-sm text-red-700">
                  Continue with "{parentEmail}" (creates separate parent record)
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onContinueAnyway}
                className="text-red-600 border-red-200 hover:bg-red-100"
              >
                Continue Anyway
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};